/**
 * Helpers de presentación / derivación del Optimizador de Cortes.
 *
 * Puro formato y cálculo de UI; sin lógica remota ni acceso a la API
 * (`docs/frontend_architecture.md`, Pr. 6). Replican el formato del SSR
 * (`static/cutlist/cutlist.js`): m² desde cm², porcentajes, fecha del historial,
 * colores derivados de la paleta y el recomendador de lámina por área.
 */
import { ApiError } from "@/lib/api";

import { CUTLIST_GENERIC_ERROR, PRESETS, SVG_PAD } from "./constants";
import type { Part } from "./types";

/**
 * "Cromo" fijo del SVG del tablero (px que NO escalan con la lámina): el margen
 * `SVG_PAD` a cada lado más el espacio reservado para las cotas/marcadores
 * (`+20` en `SheetSvg`). Se descuenta del área del contenedor antes de calcular la
 * escala fit, para que el SVG completo (no solo el rectángulo del tablero) quepa.
 */
export const SVG_CHROME = SVG_PAD * 2 + 20;

/** Margen por defecto del fit (5%): deja un respiro alrededor de la lámina. */
export const FIT_MARGIN = 0.05;

/** Escala fit mínima de respaldo cuando no se puede medir (≈ la base histórica). */
const FIT_FALLBACK_SCALE = 0.32;

/**
 * Calcula la escala base para que un tablero de `sheetW × sheetH` cm **llene** un
 * contenedor de `containerW × containerH` px (visor fit-to-container), dejando un
 * `margin` proporcional de respiro. Reemplaza el antiguo `BASE_SCALE = 0.32` fijo
 * (feature #76): el visor ya no sale diminuto, se ajusta al espacio disponible.
 *
 * Función PURA y determinista (sin medir el DOM): el componente le pasa las medidas
 * reales del contenedor (vía `ResizeObserver`) y aquí solo se resuelve la aritmética
 * — testeable sin layout real (jsdom no lo hace).
 *
 * El SVG tiene un "cromo" fijo (`chrome`, no escala: padding + cotas), así que la
 * escala se resuelve sobre el espacio útil tras descontarlo en ambos ejes y se toma
 * el eje más restrictivo. Ante medidas no utilizables (contenedor o lámina ≤ 0, o
 * espacio útil agotado por el cromo) devuelve un respaldo seguro (> 0), nunca 0/NaN.
 */
export function computeFitScale(
  containerW: number,
  containerH: number,
  sheetW: number,
  sheetH: number,
  margin = FIT_MARGIN,
  chrome = SVG_CHROME,
): number {
  // Medidas no fiables (contenedor aún sin layout, o lámina inválida): respaldo.
  if (
    !Number.isFinite(containerW) ||
    !Number.isFinite(containerH) ||
    !Number.isFinite(sheetW) ||
    !Number.isFinite(sheetH) ||
    containerW <= 0 ||
    containerH <= 0 ||
    sheetW <= 0 ||
    sheetH <= 0
  ) {
    return FIT_FALLBACK_SCALE;
  }

  const m = Math.min(Math.max(margin, 0), 0.9);
  // Espacio útil para la superficie escalable, tras el margen y el cromo fijo.
  const availW = containerW * (1 - m) - chrome;
  const availH = containerH * (1 - m) - chrome;
  if (availW <= 0 || availH <= 0) {
    return FIT_FALLBACK_SCALE;
  }

  // Eje más restrictivo: la lámina llena el contenedor sin desbordarlo.
  const scale = Math.min(availW / sheetW, availH / sheetH);
  return scale > 0 ? scale : FIT_FALLBACK_SCALE;
}

/**
 * Traduce un error de la API/red a un mensaje legible. La validación del backend
 * (`{error_code, message}`: "Debe incluir al menos una pieza"…) ya viene en español;
 * se usa el `message` del `ApiError` o del `Error`, con un texto de respaldo.
 */
export function cutlistErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return CUTLIST_GENERIC_ERROR;
}

/** Área en m² a partir de un valor en cm² (réplica de `/ 10000` del SSR). */
export function cm2ToM2(areaCm2: number, decimals = 3): string {
  return (areaCm2 / 10000).toFixed(decimals);
}

/** Área de un rectángulo (ancho × alto en cm) expresada en m². */
export function rectM2(width: number, height: number, decimals = 4): string {
  return cm2ToM2(width * height, decimals);
}

/**
 * Redondea una medida en cm matando el ruido de coma flotante que arrastra el
 * packer (`19.9999999999962` → `20`, `29.9999999999996` → `30`) sin sacrificar
 * decimales legítimos (`19.5` → `19.5`). Devuelve un `number` ya sin cola de
 * periódicos, listo para rotular en el visor.
 *
 * Toda etiqueta de medida del SVG (`SheetSvg`) debe pasar por aquí: si un valor
 * crudo del `optimizer` llega al render, reaparece el `19.9999…` que se ve en
 * pantalla. Redondear no es opcional ni caso por caso; es la regla del visor.
 */
export function roundCm(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Fecha del historial formateada en locale (réplica de `toLocaleString` del SSR). */
export function formatJobDate(createdAt: string | null): string {
  if (!createdAt) {
    return "";
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString("es-CO");
}

// ─── Utilidades de color (réplica de hexRgb/rgba/darken del SSR) ─────────────

function hexRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Color hex con alfa (`rgba(...)`), igual que `rgba()` del SSR. */
export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Oscurece un color hex restando `n` a cada canal, igual que `darken()` del SSR. */
export function darken(hex: string, n = 55): string {
  const [r, g, b] = hexRgb(hex);
  return `rgb(${Math.max(0, r - n)},${Math.max(0, g - n)},${Math.max(0, b - n)})`;
}

/** Una recomendación de lámina (un preset evaluado contra las piezas). */
export interface StockRecommendation {
  id: "std" | "small" | "custom";
  label: string;
  w: number | null;
  h: number | null;
  desc: string;
  sheetArea: number;
  estimatedSheets: number;
  efficiency: number;
  noFit: number;
  allFit: boolean;
}

/**
 * Recomendador de lámina (port VERBATIM de `calcRecommendation` del SSR): estima, por
 * cada preset de medida, cuántas láminas harían falta y qué eficiencia daría, según el
 * área total de las piezas (expandidas por cantidad). Ordena por: caben todas, menos
 * láminas, mayor eficiencia. Devuelve `null` si no hay piezas.
 */
export function calcRecommendation(parts: Part[]): StockRecommendation[] | null {
  if (!parts || parts.length === 0) return null;
  const expanded: Part[] = [];
  parts.forEach((p) => {
    for (let i = 0; i < Math.max(1, Number(p.quantity) || 1); i++) expanded.push(p);
  });
  const totalArea = expanded.reduce((s, p) => s + p.width * p.height, 0);

  return PRESETS.filter((p) => p.w)
    .map((preset) => {
      const SW = preset.w as number,
        SH = preset.h as number;
      const sheetArea = SW * SH;
      const fits = expanded.filter(
        (p) =>
          (p.width <= SW && p.height <= SH) ||
          (p.canRotate && p.height <= SW && p.width <= SH),
      );
      const noFit = expanded.length - fits.length;
      const estimatedSheets = Math.ceil(totalArea / (sheetArea * 0.85));
      const efficiency = Math.min(
        100,
        Math.round((totalArea / (estimatedSheets * sheetArea)) * 100),
      );
      const maxPieceW = Math.max(
        ...parts.map((p) => Math.max(p.width, p.canRotate ? p.height : 0)),
      );
      const widthFit = maxPieceW / SW;
      const widthBonus = widthFit > 0.7 && widthFit <= 1 ? 10 : 0;
      return {
        id: preset.id,
        label: preset.label,
        w: preset.w,
        h: preset.h,
        desc: preset.desc,
        sheetArea,
        estimatedSheets,
        efficiency: efficiency + widthBonus,
        noFit,
        allFit: noFit === 0,
      };
    })
    .sort((a, b) => {
      if (a.allFit !== b.allFit) return a.allFit ? -1 : 1;
      if (a.estimatedSheets !== b.estimatedSheets)
        return a.estimatedSheets - b.estimatedSheets;
      return b.efficiency - a.efficiency;
    });
}
